import { useCallback, useMemo } from 'react';

interface PerformanceMetrics {
  renderTime: number;
  searchTime: number;
  filterTime: number;
  memoryUsage?: number;
}

/**
 * Performance monitoring utility for tracking render times, async operations,
 * and memory usage in development environments.
 *
 * Features:
 * - Timing measurements for operations
 * - Memory usage tracking
 * - Long task detection
 * - Layout shift monitoring
 * - Automatic warnings for slow operations
 */
class PerformanceMonitor {
  private metrics: Map<string, number> = new Map();
  private observers: PerformanceObserver[] = [];

  startTiming(label: string): void {
    this.metrics.set(`${label}_start`, performance.now());
  }

  endTiming(label: string): number {
    const startTime = this.metrics.get(`${label}_start`);
    if (startTime === undefined) {
      console.warn(`No start time found for label: ${label}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.metrics.set(label, duration);
    this.metrics.delete(`${label}_start`);

    return duration;
  }

  measureRender<T>(fn: () => T, label: string = 'render'): T {
    this.startTiming(label);
    const result = fn();
    const duration = this.endTiming(label);

    if (duration > 16) {
      // More than one frame at 60fps
      console.warn(
        `Slow render detected: ${label} took ${duration.toFixed(2)}ms`
      );
    }

    return result;
  }

  async measureAsync<T>(fn: () => Promise<T>, label: string): Promise<T> {
    this.startTiming(label);
    try {
      const result = await fn();
      const duration = this.endTiming(label);

      if (duration > 100) {
        // Arbitrary threshold for async operations
        console.warn(
          `Slow async operation: ${label} took ${duration.toFixed(2)}ms`
        );
      }

      return result;
    } catch (error) {
      this.endTiming(label);

      // Enhanced error logging for blockchain operations
      if (error instanceof Error && error.message.includes('RPC')) {
        console.error(`Blockchain RPC error in ${label}:`, {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
        });
      }

      throw error;
    }
  }

  getMetrics(): PerformanceMetrics {
    return {
      renderTime: this.metrics.get('render') || 0,
      searchTime: this.metrics.get('search') || 0,
      filterTime: this.metrics.get('filter') || 0,
      memoryUsage: this.getMemoryUsage(),
    };
  }

  private getMemoryUsage(): number | undefined {
    if (
      typeof window !== 'undefined' &&
      typeof performance !== 'undefined' &&
      'memory' in performance
    ) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return undefined;
  }

  observeLongTasks(): void {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              // Long task threshold
              console.warn(
                `Long task detected: ${entry.duration.toFixed(2)}ms`
              );
            }
          }
        });

        observer.observe({ entryTypes: ['longtask'] });
        this.observers.push(observer);
      } catch (error) {
        console.warn('Long task observation not supported');
      }
    }
  }

  observeLayoutShifts(): void {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) {
            if ((entry as any).value > 0.1) {
              // CLS threshold
              console.warn(`Layout shift detected: ${(entry as any).value}`);
            }
          }
        });

        observer.observe({ entryTypes: ['layout-shift'] });
        this.observers.push(observer);
      } catch (error) {
        console.warn('Layout shift observation not supported');
      }
    }
  }

  disconnect(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics.clear();
  }

  logMetrics(): void {
    const metrics = this.getMetrics();
    console.group('Performance Metrics');
    console.log('Render Time:', `${metrics.renderTime.toFixed(2)}ms`);
    console.log('Search Time:', `${metrics.searchTime.toFixed(2)}ms`);
    console.log('Filter Time:', `${metrics.filterTime.toFixed(2)}ms`);
    if (metrics.memoryUsage) {
      console.log(
        'Memory Usage:',
        `${(metrics.memoryUsage / 1024 / 1024).toFixed(2)}MB`
      );
    }
    console.groupEnd();
  }
}

export const performanceMonitor = new PerformanceMonitor();

/**
 * React hook for performance monitoring in components.
 * Provides convenient methods for timing operations and measuring performance.
 *
 * @param enabled - Whether monitoring is enabled (defaults to development mode)
 * @returns Object with performance monitoring methods
 */
export const usePerformanceMonitoring = (
  enabled: boolean = process.env.NODE_ENV === 'development'
) => {
  const startTiming = useCallback(
    (label: string) => {
      if (enabled) {
        performanceMonitor.startTiming(label);
      }
    },
    [enabled]
  );

  const endTiming = useCallback(
    (label: string) => {
      if (enabled) {
        return performanceMonitor.endTiming(label);
      }
      return 0;
    },
    [enabled]
  );

  const measureRender = useCallback(
    <T>(fn: () => T, label: string = 'render'): T => {
      if (enabled) {
        return performanceMonitor.measureRender(fn, label);
      }
      return fn();
    },
    [enabled]
  );

  const measureAsync = useCallback(
    async <T>(fn: () => Promise<T>, label: string): Promise<T> => {
      if (enabled) {
        return performanceMonitor.measureAsync(fn, label);
      }
      return fn();
    },
    [enabled]
  );

  const getMetrics = useCallback(
    () => (enabled ? performanceMonitor.getMetrics() : null),
    [enabled]
  );
  const logMetrics = useCallback(() => {
    if (enabled) performanceMonitor.logMetrics();
  }, [enabled]);

  return useMemo(
    () => ({
      startTiming,
      endTiming,
      measureRender,
      measureAsync,
      getMetrics,
      logMetrics,
    }),
    [
      startTiming,
      endTiming,
      measureRender,
      measureAsync,
      getMetrics,
      logMetrics,
    ]
  );
};

/**
 * Query performance monitoring utilities
 */
export const queryPerformance = {
  getCacheMetrics: () => ({
    cacheHits: 0,
    cacheMisses: 0,
    cacheSize: 0,
    averageQueryTime: 0,
  }),
};

/**
 * Bundle analysis utilities
 */
export const bundleAnalysis = {
  getLazyLoadingStats: () => ({
    loadedModules: 0,
    totalModules: 0,
    lazyLoadRatio: 0,
    bundleSize: 0,
  }),
};
